/**
 * The visible half of the saved-vehicle garage: the picker, the header chip, the
 * product page verdict, the card badges, and the narrowing on collection and
 * search pages.
 *
 * All state lives in aoem-garage.js. Nothing here writes to storage directly; it
 * calls AoemGarage.set / .clear and re-renders when aoem:vehicle-change fires.
 */
(function () {
  'use strict';

  var garage = window.AoemGarage;
  if (!garage) return;

  var CASCADE = ['year', 'make', 'model', 'submodel', 'engine'];
  var VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/i;

  // ── shared helpers ────────────────────────────────────────────────────────

  /**
   * Shared across every picker on the page, which matters because the search page
   * carries two: the one in the header drawer and the one above the results. Both
   * ask for the same year list on load, and the reference cascade is rate limited,
   * so without this they compete and one of them loses.
   *
   * The cascade is reference data and does not change during a visit, so caching
   * for the life of the page is safe.
   */
  var optionsCache = {};

  function proxy(path) {
    if (optionsCache[path]) return optionsCache[path];

    var request = fetch(garage.config.proxyBase + path, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) {
          var error = new Error('HTTP ' + response.status);
          error.status = response.status;
          throw error;
        }
        return response.json();
      })
      .then(function (json) {
        // The API wraps responses as { message, result }; unwrap if present.
        return json && json.result !== undefined ? json.result : json;
      })
      .catch(function (error) {
        // A failure must not be cached, or a transient blip becomes permanent.
        delete optionsCache[path];
        throw error;
      });

    optionsCache[path] = request;
    return request;
  }

  function messageFor(error, fallback) {
    return error && error.status === 429
      ? 'Too many searches just now. Please wait a moment and try again.'
      : fallback;
  }

  function setStatus(node, text, kind) {
    if (!node) return;
    if (!text) {
      node.hidden = true;
      node.textContent = '';
      node.removeAttribute('data-kind');
      return;
    }
    node.hidden = false;
    node.textContent = text;
    node.setAttribute('data-kind', kind || 'info');
  }

  // ── combobox ──────────────────────────────────────────────────────────────

  /**
   * A filtering combobox over a list of options, matching the one the homepage app
   * block uses. A single model year can offer well over a hundred makes, so the
   * shopper types a few characters rather than scrolling, and the two pickers behave
   * the same way.
   *
   * Follows the ARIA combobox pattern: the input owns the listbox and reports the
   * active option through aria-activedescendant, so focus stays in the field while
   * arrowing through options.
   */
  function createCombobox(root, liveEl, onChange) {
    var input = root.querySelector('[data-aoem-combo-input]');
    var toggle = root.querySelector('[data-aoem-combo-toggle]');
    var list = root.querySelector('[data-aoem-combo-list]');

    var options = [];
    var matches = [];
    var selected = null;
    var activeIndex = -1;
    var isOpen = false;

    function open() {
      if (isOpen || input.disabled) return;
      isOpen = true;
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      root.classList.add('aoem-combo--open');
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      root.classList.remove('aoem-combo--open');
      activeIndex = -1;
    }

    function filter(text) {
      var query = (text || '').trim().toLowerCase();

      if (!query) {
        matches = options.slice();
        return;
      }

      matches = options.filter(function (option) {
        return option.label.toLowerCase().indexOf(query) !== -1;
      });

      // Someone typing "co" for Corolla means the ones that start that way.
      matches.sort(function (a, b) {
        var aPrefix = a.label.toLowerCase().indexOf(query) === 0 ? 0 : 1;
        var bPrefix = b.label.toLowerCase().indexOf(query) === 0 ? 0 : 1;
        return aPrefix - bPrefix;
      });
    }

    function renderList() {
      list.innerHTML = '';

      if (!matches.length) {
        var empty = document.createElement('li');
        empty.className = 'aoem-combo__empty';
        empty.textContent = 'No matches';
        list.appendChild(empty);
        setActive(-1);
        return;
      }

      matches.forEach(function (option, index) {
        var item = document.createElement('li');
        item.className = 'aoem-combo__option';
        item.id = list.id + '-opt-' + index;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.textContent = option.label;

        // pointerdown, not click: the input blurs first and would close the list.
        // Pointer events also cover touch, which click ordering does not do
        // reliably on iOS.
        item.addEventListener('pointerdown', function (event) {
          event.preventDefault();
          choose(index);
        });

        list.appendChild(item);
      });

      setActive(0);
    }

    function setActive(index) {
      var items = list.querySelectorAll('.aoem-combo__option');

      for (var i = 0; i < items.length; i++) {
        var active = i === index;
        items[i].classList.toggle('aoem-combo__option--active', active);
        items[i].setAttribute('aria-selected', active ? 'true' : 'false');
      }

      activeIndex = index;

      if (index >= 0 && items[index]) {
        input.setAttribute('aria-activedescendant', items[index].id);
        items[index].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function choose(index) {
      var option = matches[index];
      if (!option) return;

      selected = option;
      input.value = option.label;
      close();
      onChange(option.value);
    }

    // Leaving free text behind would imply a selection that was never made.
    function revert() {
      input.value = selected ? selected.label : '';
    }

    function announce(count) {
      if (!liveEl) return;
      liveEl.textContent = count === 1 ? '1 option available' : count + ' options available';
    }

    input.addEventListener('input', function () {
      // Typing invalidates a previous pick, so downstream levels must clear too.
      if (selected) {
        selected = null;
        onChange('');
      }

      filter(input.value);
      renderList();
      open();
      announce(matches.length);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();

        if (!isOpen) {
          filter('');
          renderList();
          open();
          return;
        }

        var step = event.key === 'ArrowDown' ? 1 : -1;
        if (matches.length) setActive((activeIndex + step + matches.length) % matches.length);
        return;
      }

      if (event.key === 'Enter' && isOpen) {
        event.preventDefault();
        choose(activeIndex);
        return;
      }

      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        revert();
        close();
        return;
      }

      if (event.key === 'Tab') close();
    });

    input.addEventListener('focus', function () {
      if (input.disabled) return;
      // Showing the whole list on focus keeps it usable for anyone who would rather
      // browse than type.
      filter(selected ? '' : input.value);
      renderList();
      open();
    });

    input.addEventListener('blur', function () {
      revert();
      close();
    });

    toggle.addEventListener('pointerdown', function (event) {
      event.preventDefault();

      if (isOpen) {
        close();
        return;
      }

      // Open directly rather than leaning on the focus handler. If the input is
      // already focused - after an Escape, say - focus() is a no-op, no focus event
      // fires, and the toggle would appear dead until clicked a second time.
      input.focus();
      filter(selected ? '' : input.value);
      renderList();
      open();
    });

    return {
      setOptions: function (items) {
        options = items;
        selected = null;
        input.value = '';
        input.disabled = false;
        toggle.disabled = false;
        root.classList.remove('aoem-combo--loading');
      },
      setLoading: function () {
        options = [];
        selected = null;
        input.value = '';
        input.disabled = true;
        toggle.disabled = true;
        root.classList.add('aoem-combo--loading');
        close();
      },
      reset: function () {
        options = [];
        selected = null;
        input.value = '';
        input.disabled = true;
        toggle.disabled = true;
        root.classList.remove('aoem-combo--loading');
        close();
      },
      value: function () {
        return selected ? selected.value : '';
      },
      label: function () {
        return selected ? selected.label : '';
      }
    };
  }

  // ── picker ────────────────────────────────────────────────────────────────

  function initPicker(root) {
    if (root.dataset.aoemReady) return;
    root.dataset.aoemReady = 'true';

    var status = root.querySelector('[data-aoem-picker-status]');
    var live = root.querySelector('[data-aoem-combo-live]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-aoem-picker-tab]'));
    var panels = {};
    var combos = {};
    var token = 0;

    tabs.forEach(function (tab) {
      panels[tab.dataset.aoemPickerTab] = root.querySelector(
        '[data-aoem-picker-panel="' + tab.dataset.aoemPickerTab + '"]'
      );

      tab.addEventListener('click', function () {
        selectTab(tab.dataset.aoemPickerTab);
      });

      tab.addEventListener('keydown', function (event) {
        var step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        var next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
        next.focus();
        selectTab(next.dataset.aoemPickerTab);
      });
    });

    function selectTab(name) {
      tabs.forEach(function (tab) {
        var active = tab.dataset.aoemPickerTab === name;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (panels[tab.dataset.aoemPickerTab]) panels[tab.dataset.aoemPickerTab].hidden = !active;
      });
      setStatus(status, '');
    }

    // Year/Make/Model cascade.

    CASCADE.forEach(function (level, index) {
      var node = root.querySelector('[data-aoem-combo="' + level + '"]');
      if (!node) return;

      combos[level] = createCombobox(node, live, function (value) {
        // Everything downstream of the changed level is now meaningless.
        for (var i = index + 1; i < CASCADE.length; i++) resetLevel(CASCADE[i]);
        setStatus(status, '');

        var next = CASCADE[index + 1];
        if (value && next) loadLevel(next);
        refreshSubmit();
      });
    });

    function resetLevel(level) {
      if (combos[level]) combos[level].reset();
    }

    function loadLevel(level) {
      var combo = combos[level];
      if (!combo) return;

      var mine = ++token;
      combo.setLoading();

      proxy(pathFor(level))
        .then(function (items) {
          // A slow answer for an abandoned selection must not overwrite a newer one.
          if (mine !== token) return;

          combo.setOptions(
            (items || []).map(function (item) {
              return { value: String(item.id), label: item.label };
            })
          );
        })
        .catch(function (error) {
          if (mine !== token) return;
          resetLevel(level);
          setStatus(status, messageFor(error, 'Could not load vehicle options. Please try again.'), 'error');
        });
    }

    function pathFor(level) {
      var year = '?year=' + encodeURIComponent(value('year'));
      var make = '&makeId=' + encodeURIComponent(value('make'));
      var model = '&modelId=' + encodeURIComponent(value('model'));
      var submodel = '&subModelId=' + encodeURIComponent(value('submodel'));

      if (level === 'year') return '/vehicle/years';
      if (level === 'make') return '/vehicle/makes' + year;
      if (level === 'model') return '/vehicle/models' + year + make;
      if (level === 'submodel') return '/vehicle/submodels' + year + make + model;
      return '/vehicle/engines' + year + make + model + submodel;
    }

    function value(level) {
      return combos[level] ? combos[level].value() : '';
    }

    function complete() {
      return CASCADE.every(function (level) {
        return value(level);
      });
    }

    function refreshSubmit() {
      var submit = panels.ymme && panels.ymme.querySelector('[data-aoem-picker-submit]');
      if (submit) submit.disabled = !complete();
    }

    if (panels.ymme) {
      panels.ymme.addEventListener('submit', function (event) {
        event.preventDefault();
        if (!complete()) return;

        commit(
          {
            kind: 'ymme',
            year: value('year'),
            makeId: value('make'),
            modelId: value('model'),
            subModelId: value('submodel'),
            engineBaseId: value('engine'),
            // A provisional label so the chip reads sensibly before the API answers.
            label: [combos.year.label(), combos.make.label(), combos.model.label()]
              .filter(Boolean)
              .join(' ')
          },
          panels.ymme.querySelector('[data-aoem-picker-submit]')
        );
      });
    }

    if (panels.vin) {
      panels.vin.addEventListener('submit', function (event) {
        event.preventDefault();
        var input = panels.vin.querySelector('[data-aoem-vin-input]');
        var vin = (input.value || '').trim().toUpperCase();

        if (!VIN_PATTERN.test(vin)) {
          setStatus(status, 'Enter a valid 17-character VIN.', 'error');
          input.focus();
          return;
        }
        commit({ kind: 'vin', vin: vin }, panels.vin.querySelector('[data-aoem-picker-submit]'));
      });
    }

    /**
     * Saving the vehicle is what triggers the fitment lookup, so the picker waits
     * for it: a vehicle the API cannot match should report that here rather than
     * silently leaving the shopper on a page that has not changed.
     */
    function commit(vehicle, submit) {
      if (submit) submit.disabled = true;
      setStatus(status, 'Checking fitment\u2026', 'info');

      garage.set(vehicle);

      garage
        .fitment()
        .then(function (result) {
          if (!result) return;

          if (!result.handles.length) {
            setStatus(status, 'No converters currently listed for ' + result.label + '.', 'info');
          } else {
            setStatus(status, '');
            root.dispatchEvent(new CustomEvent('aoem:picker-done', { bubbles: true, detail: result }));
          }
        })
        .catch(function (error) {
          setStatus(status, messageFor(error, 'Could not check fitment just now. Please try again.'), 'error');
        })
        .finally(function () {
          if (submit) submit.disabled = false;
        });
    }

    resetLevel('make');
    resetLevel('model');
    resetLevel('submodel');
    resetLevel('engine');
    loadLevel('year');
    refreshSubmit();
  }

  function initPickers(scope) {
    (scope || document).querySelectorAll('[data-aoem-picker]').forEach(initPicker);
  }

  // ── header chip and drawer ────────────────────────────────────────────────

  /**
   * The saved-vehicle bar and the drawer it opens.
   *
   * One control at every width. It started as a header chip with a bar only on
   * mobile, but on production the chip pushed the navigation onto a second line, so
   * the bar does the job alone.
   */
  function initControls() {
    var bar = document.querySelector('[data-aoem-garage-bar]');
    var drawer = document.querySelector('[data-aoem-garage-drawer]');
    if (!bar) return;

    var button = bar.querySelector('[data-aoem-bar-button]');
    var label = bar.querySelector('[data-aoem-bar-label]');
    var action = bar.querySelector('[data-aoem-bar-action]');
    var clear = bar.querySelector('[data-aoem-bar-clear]');
    var lastFocus = null;

    function open() {
      if (!drawer) return;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      drawer.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');

      // The first usable combobox, so a keyboard user lands where the work is.
      var first =
        drawer.querySelector('[data-aoem-combo-input]:not([disabled])') ||
        drawer.querySelector('button, input');
      if (first) first.focus();
    }

    function close() {
      if (!drawer) return;
      drawer.hidden = true;
      drawer.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      if (lastFocus) lastFocus.focus();
    }

    button.addEventListener('click', open);
    clear.addEventListener('click', function () {
      garage.clear();
    });

    // Anything else needing the picker goes through this rather than synthesising a
    // click on the bar.
    if (drawer) openPicker = open;

    if (drawer) {
      // The backdrop, but not the panel inside it.
      drawer.addEventListener('click', function (event) {
        if (event.target === drawer) close();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !drawer.hidden) close();
      });

      // Picking a vehicle is the drawer's whole purpose, so it closes on success.
      drawer.addEventListener('aoem:picker-done', close);
    }

    function render() {
      var vehicle = garage.get();

      if (!vehicle) {
        bar.classList.remove('aoem-garage-bar--set');
        label.textContent = 'Find your converter';
        action.textContent = 'Select';
        clear.hidden = true;
        return;
      }

      bar.classList.add('aoem-garage-bar--set');
      label.textContent = garage.label(vehicle);
      action.textContent = 'Change';
      clear.hidden = false;

      // A vehicle restored from storage may predate the API describing it, so the
      // label is refreshed once the fitment answer lands.
      garage
        .fitment()
        .then(function (result) {
          if (result && result.label) label.textContent = result.label;
        })
        .catch(function () {
          /* Leave the stored label alone; the bar is not worth an error state. */
        });
    }

    render();
    document.addEventListener(garage.EVENT, render);
  }

  // ── product page verdict ──────────────────────────────────────────────────

  var ICONS = {
    neutral: '<circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16.5v.01"/>',
    fits: '<circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/>',
    no: '<circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/>'
  };

  /**
   * Opens the picker drawer from anywhere. Set by initControls.
   *
   * Callers must not click the chip directly: it is hidden below 990px, and a click
   * on a hidden element does nothing, which would leave the product page's "Choose
   * your vehicle" link dead on mobile.
   */
  var openPicker = function () {
    window.location.href = garage.config.routeRoot + 'search';
  };

  function initFitCheck() {
    var box = document.querySelector('[data-aoem-fitcheck]');
    if (!box) return;

    var handle = box.dataset.handle;
    var icon = box.querySelector('[data-aoem-fitcheck-icon]');
    var title = box.querySelector('[data-aoem-fitcheck-title]');
    var note = box.querySelector('[data-aoem-fitcheck-note]');

    function paint(state, titleText, noteHtml) {
      box.classList.remove('aoem-fitcheck--fits', 'aoem-fitcheck--no');
      if (state !== 'neutral') box.classList.add('aoem-fitcheck--' + state);
      icon.innerHTML = ICONS[state];
      title.textContent = titleText;
      note.innerHTML = noteHtml;

      var choose = note.querySelector('[data-aoem-fitcheck-choose]');
      if (choose) choose.addEventListener('click', function () { openPicker(); });
    }

    function render() {
      var vehicle = garage.get();

      if (!vehicle) {
        paint(
          'neutral',
          'Check this part against your vehicle',
          '<button type="button" class="aoem-fitcheck__action" data-aoem-fitcheck-choose>Choose your vehicle</button> to confirm this converter is the right fit.'
        );
        return;
      }

      garage
        .fitment()
        .then(function (result) {
          if (!result) return;

          var name = escapeHtml(result.label);

          if (result.handles.indexOf(handle) !== -1) {
            paint('fits', 'Fits your ' + result.label, 'Matched by OEM part number and VIN-level fitment data.');
            return;
          }

          // Being absent from the fitting set is only meaningful when that set has
          // something in it. An empty set means we list nothing for this vehicle
          // yet, which is not the same as this part being wrong.
          if (!result.handles.length) {
            paint(
              'neutral',
              'No converters listed for your ' + result.label + ' yet',
              'We cannot confirm this part against that vehicle. <a class="aoem-fitcheck__action" href="' +
                garage.config.routeRoot + 'pages/contact">Ask us to check</a>.'
            );
            return;
          }

          paint(
            'no',
            'Does not fit your ' + result.label,
            '<a class="aoem-fitcheck__action" href="' + garage.config.routeRoot +
              'collections/all?vehicle=' + encodeURIComponent(garage.keyOf(vehicle)) +
              '">See the ' + result.handles.length + ' converter' + (result.handles.length === 1 ? '' : 's') +
              ' that fit your ' + name + '</a>.'
          );
        })
        .catch(function () {
          paint(
            'neutral',
            'Could not check fitment just now',
            'The fitment service did not respond. Please try again shortly.'
          );
        });
    }

    render();
    document.addEventListener(garage.EVENT, render);
  }

  function escapeHtml(text) {
    var node = document.createElement('span');
    node.textContent = text == null ? '' : text;
    return node.innerHTML;
  }

  // ── narrowing collection and search results ───────────────────────────────

  /**
   * Reduces the grid to the parts that fit the saved vehicle.
   *
   * Cards already on the page are hidden rather than removed, so restoring them
   * costs nothing. Fitting parts that are not on the page - because Dawn paginates
   * and the fitting set does not respect page boundaries - are fetched and appended,
   * which is what makes the count on the notice honest rather than "whatever
   * happened to be on page one".
   */
  function initNarrowing() {
    var notice = document.querySelector('[data-aoem-narrow]');
    var grid = document.getElementById('product-grid');
    if (!notice || !grid) return;

    // #product-grid is the <ul> itself on a collection page but a wrapper around
    // one on the search page, so both shapes have to be handled.
    var list =
      grid.tagName === 'UL' ? grid : grid.querySelector('ul.product-grid') || grid.querySelector('ul');
    if (!list) return;

    var text = notice.querySelector('[data-aoem-narrow-text]');
    var toggle = notice.querySelector('[data-aoem-narrow-toggle]');
    var appended = [];
    var emptyNote = null;

    toggle.addEventListener('click', function () {
      if (garage.isShowingAll()) garage.narrowAgain();
      else garage.showAll();
    });

    function cardHandle(card) {
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return null;
      var match = /\/products\/([^/?#]+)/.exec(link.getAttribute('href'));
      return match ? decodeURIComponent(match[1]) : null;
    }

    function restore() {
      list.querySelectorAll('.aoem-hidden-by-fitment').forEach(function (card) {
        card.classList.remove('aoem-hidden-by-fitment');
      });
      appended.forEach(function (node) {
        node.remove();
      });
      appended = [];
      if (emptyNote) {
        emptyNote.remove();
        emptyNote = null;
      }
      setPaginationHidden(false);
      setCount(null);
    }

    function setPaginationHidden(hidden) {
      // While narrowed the grid holds the complete fitting set, so Dawn's paging
      // through the unnarrowed catalogue would be misleading.
      var pagination = document.querySelector('.pagination-wrapper');
      if (pagination) pagination.hidden = hidden;
    }

    /**
     * Dawn renders the count server-side from the unnarrowed collection, so it has
     * to be corrected. Leaving "12 products" above three cards reads as a bug and
     * undermines the one number the shopper is relying on.
     */
    var counters = document.querySelectorAll('#ProductCount, #ProductCountDesktop');

    function setCount(n) {
      counters.forEach(function (node) {
        if (!node.dataset.aoemOriginal) node.dataset.aoemOriginal = node.textContent;
        node.textContent = n === null
          ? node.dataset.aoemOriginal
          : n + (n === 1 ? ' product' : ' products');
      });
    }

    function render() {
      var vehicle = garage.get();

      if (!vehicle) {
        notice.classList.remove('is-active');
        restore();
        return;
      }

      garage
        .fitment()
        .then(function (result) {
          if (!result) return;

          notice.classList.add('is-active');

          if (garage.isShowingAll()) {
            restore();
            text.innerHTML =
              'Showing all parts. <span class="aoem-narrow__vehicle">' +
              escapeHtml(result.label) + '</span> is still saved.';
            toggle.textContent = 'Show only parts that fit';
            return;
          }

          var fitting = result.handles;
          text.innerHTML =
            'Showing ' + fitting.length + ' part' + (fitting.length === 1 ? '' : 's') +
            ' that fit your <span class="aoem-narrow__vehicle">' + escapeHtml(result.label) + '</span>';
          toggle.textContent = 'Show all parts';

          restore();

          var onPage = {};
          list.querySelectorAll('li').forEach(function (card) {
            var handle = cardHandle(card);
            if (handle && fitting.indexOf(handle) !== -1) onPage[handle] = true;
            else card.classList.add('aoem-hidden-by-fitment');
          });

          setPaginationHidden(true);
          setCount(fitting.length);

          if (!fitting.length) {
            emptyNote = document.createElement('p');
            emptyNote.className = 'aoem-narrow__empty';
            emptyNote.textContent =
              'We do not list a converter for your ' + result.label + ' yet.';
            list.insertAdjacentElement('afterend', emptyNote);
            return;
          }

          var missing = fitting.filter(function (handle) {
            return !onPage[handle];
          });
          if (missing.length) appendMissing(missing);
        })
        .catch(function () {
          // A failed lookup must not leave a half-filtered grid behind.
          notice.classList.remove('is-active');
          restore();
        });
    }

    function appendMissing(handles) {
      Promise.all(handles.map(fetchCard)).then(function (cards) {
        cards.filter(Boolean).forEach(function (card) {
          list.appendChild(card);
          appended.push(card);
        });
      });
    }

    /**
     * Builds a card for a product that is not on this page. Dawn's own card markup
     * is only available server-side, so this mirrors its class names to inherit the
     * same styling rather than introducing a second card design.
     */
    function fetchCard(handle) {
      return fetch(garage.config.routeRoot + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (product) {
          if (!product) return null;

          var li = document.createElement('li');
          li.className = 'grid__item';

          var image = product.featured_image
            ? '<div class="card__media"><div class="media media--transparent media--hover-effect">' +
              '<img src="' + escapeHtml(product.featured_image) + '" alt="' + escapeHtml(product.title) +
              '" loading="lazy" width="400" height="400"></div></div>'
            : '';

          li.innerHTML =
            '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
            '<div class="card card--card card--media">' +
            '<div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 100%;">' +
            image +
            '</div>' +
            '<div class="card__content"><div class="card__information">' +
            '<h3 class="card__heading h5"><a href="' + garage.config.routeRoot + 'products/' +
            encodeURIComponent(handle) + '" class="full-unstyled-link">' + escapeHtml(product.title) + '</a></h3>' +
            '<div class="card-information"><span class="price">' +
            formatMoney(product.price) + '</span></div>' +
            '<span class="aoem-card-fit" data-state="fits">Fits your vehicle</span>' +
            '</div></div></div></div>';

          return li;
        })
        .catch(function () {
          return null;
        });
    }

    function formatMoney(cents) {
      if (typeof cents !== 'number') return '';
      // Shopify's money format is not exposed to JS here; the store sells in USD
      // and the card only needs the amount to read correctly.
      return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    render();
    document.addEventListener(garage.EVENT, render);
  }

  // ── card badges ───────────────────────────────────────────────────────────

  /**
   * Marks product cards against the saved vehicle. Runs everywhere cards appear,
   * including alongside the narrowing, where the surviving cards all fit and the
   * badge simply confirms why they are the ones left.
   */
  function initCardBadges() {
    var slots = document.querySelectorAll('[data-aoem-card-fit]');
    if (!slots.length) return;

    function render() {
      var vehicle = garage.get();

      if (!vehicle) {
        slots.forEach(function (slot) {
          slot.removeAttribute('data-state');
          slot.textContent = '';
        });
        return;
      }

      garage
        .fitment()
        .then(function (result) {
          if (!result) return;

          // With nothing listed for the vehicle there is no verdict to give, and
          // marking every card "does not fit" would be misleading.
          if (!result.handles.length) return;

          slots.forEach(function (slot) {
            var fits = result.handles.indexOf(slot.dataset.handle) !== -1;
            slot.dataset.state = fits ? 'fits' : 'no';
            slot.textContent = fits ? 'Fits your vehicle' : 'Does not fit';
          });
        })
        .catch(function () {
          /* No badge is better than a wrong one. */
        });
    }

    render();
    document.addEventListener(garage.EVENT, render);
  }

  // ── boot ──────────────────────────────────────────────────────────────────

  function boot() {
    initPickers(document);
    initControls();
    initFitCheck();
    initNarrowing();
    initCardBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.AoemGarageUI = { initPickers: initPickers };
})();
